import { useEffect, useState, lazy, Suspense, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { WEB_URL } from "@/lib/webUrl";
import { routes } from "@ckgo2/shared";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { Trip, Guide, PremiumUpgrade, TripPremiumUpgrade } from "@ckgo2/shared/types";
import type { FieldErrors, UseFormReturn } from "react-hook-form";
import {
  AlertCircle,
  ArrowLeft,
  Save,
  Loader2,
  FileText,
  Map,
  Image,
  Calendar,
  Clock,
  LayoutList,
  Boxes,
  Calculator,
  BedDouble,
} from "lucide-react";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import ErrorBanner from "@/components/common/ErrorBanner";
import FieldHint from "@/components/common/FieldHint";
import TabWebLink from "@/components/common/TabWebLink";
import { MediaPickerScopeProvider } from "@/components/common/MediaPickerScope";
import { useEditPage } from "@/hooks/useEditPage";
import { useEditorTab, useSwitchTabOnSaveError } from "@/hooks/useEditorTab";
import { TRIP_FIELD_TO_TAB, getFirstTabWithErrors, getTabsWithErrors } from "@/lib/fieldTabMap";
import { coordinateInputError } from "@/lib/hotelCoordinatesInput";
import { tripHintRegistry } from "@/lib/webHints/trip";
import { tabWebTarget, tripVariant } from "@/lib/webHints/variants";
import type { TripFormData, CatalogComponent } from "@/components/trips";
import {
  TRIP_FORM_DEFAULTS,
  assignmentsAfterSave,
  tripFormFromDetail,
  tripSaveBody,
  unsavedTripParts,
  type UnsavedTripPart,
} from "@/components/trips/tripFormTransform";
import { useTripHintVariant } from "@/components/trips/useTripHintVariant";

const BasicInfoTab = lazy(() => import("@/components/trips/BasicInfoTab"));
const ItineraryTab = lazy(() => import("@/components/trips/ItineraryTab"));
const IncludedTab = lazy(() => import("@/components/trips/IncludedTab"));
const GalleryTab = lazy(() => import("@/components/trips/GalleryTab"));
const ContentTab = lazy(() => import("@/components/trips/ContentTab"));
const DatesTab = lazy(() => import("@/components/trips/DatesTab"));
const ComponentsTab = lazy(() => import("@/components/trips/ComponentsTab"));
const ExperienceTab = lazy(() => import("@/components/trips/ExperienceTab"));
const CalculationTab = lazy(() => import("@/components/trips/CalculationTab"));
const HotelTab = lazy(() => import("@/components/trips/HotelTab"));

const TABS = [
  { id: "basic", label: i18n.t("trips.edit.tabs.basic"), icon: FileText },
  { id: "itinerary", label: i18n.t("trips.edit.tabs.itinerary"), icon: Map },
  { id: "experience", label: i18n.t("trips.edit.tabs.experience"), icon: Clock },
  { id: "hotel", label: i18n.t("trips.edit.tabs.hotel"), icon: BedDouble },
  { id: "included", label: i18n.t("trips.edit.tabs.included"), icon: FileText },
  { id: "gallery", label: i18n.t("trips.edit.tabs.gallery"), icon: Image },
  { id: "dates", label: i18n.t("trips.edit.tabs.dates"), icon: Calendar },
  { id: "components", label: i18n.t("trips.edit.tabs.components"), icon: Boxes },
  { id: "calculation", label: i18n.t("trips.edit.tabs.calculation"), icon: Calculator },
  { id: "content", label: i18n.t("trips.edit.tabs.content"), icon: LayoutList },
];

function TripTabContent({
  activeTab,
  form,
  guides,
  allUpgrades,
  allTrips,
  componentCatalog,
  savedTrip,
  upgradesHint,
}: {
  activeTab: string;
  form: UseFormReturn<TripFormData>;
  guides: Guide[];
  allUpgrades: PremiumUpgrade[];
  allTrips: Trip[];
  componentCatalog: CatalogComponent[];
  /** Uložený řádek zájezdu (`null` u nového); karta „Inspirace na trase" se řídí jím. */
  savedTrip: Trip | null;
  /** Nápověda výběru upgradů; vazbu `upgradeIds` drží tahle stránka, ne záložka Obsah. */
  upgradesHint: ReactNode;
}) {
  switch (activeTab) {
    case "basic":
      return <BasicInfoTab form={form} guides={guides} />;
    case "itinerary":
      return <ItineraryTab form={form} />;
    case "included":
      return <IncludedTab form={form} />;
    case "gallery":
      return <GalleryTab form={form} />;
    case "content":
      return (
        <ContentTab
          form={form}
          allUpgrades={allUpgrades}
          allTrips={allTrips}
          savedTrip={savedTrip}
          upgradeIds={form.watch("upgradeIds") ?? []}
          onUpgradeIdsChange={(ids) => form.setValue("upgradeIds", ids, { shouldDirty: true })}
          upgradesHint={upgradesHint}
        />
      );
    case "dates":
      return <DatesTab form={form} guides={guides} />;
    case "components":
      return <ComponentsTab form={form} catalog={componentCatalog} />;
    case "calculation":
      return <CalculationTab form={form} />;
    case "experience":
      return <ExperienceTab form={form} />;
    case "hotel":
      return <HotelTab form={form} />;
    default:
      return null;
  }
}

/**
 * Pruh po založení zájezdu, kterému API neuložilo některou část (`unsavedParts`, vize F6).
 * Vzhled `ErrorBanner`, ale ne komponenta: ta bere `Error`, tady jde o úspěšnou odpověď.
 * Na záložku nepřepíná (`useSwitchTabOnSaveError` mapuje jen chyby polí).
 */
function UnsavedPartsBanner({
  parts,
  onClose,
}: {
  parts: readonly UnsavedTripPart[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Literální klíče kvůli typovanému `t()`; názvy, pod kterými redakce části v editoru najde.
  const partLabel: Record<UnsavedTripPart, string> = {
    upgrades: t("trips.edit.unsavedParts.parts.upgrades"),
    dates: t("trips.edit.unsavedParts.parts.dates"),
    components: t("trips.edit.unsavedParts.parts.components"),
  };
  return (
    <div
      data-neulozene-casti
      role="alert"
      className="bg-error-50 dark:bg-error-900/30 rounded-lg border border-red-200 p-4 dark:border-red-800/50"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="text-error-600 dark:text-error-400 mt-0.5 h-5 w-5 shrink-0" />
        <p className="text-error-700 dark:text-error-400 min-w-0 flex-1 font-medium">
          {t("trips.edit.unsavedParts.message", {
            parts: parts.map((part) => partLabel[part]).join(", "),
          })}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-error-700 dark:text-error-400 shrink-0 text-sm font-medium underline"
        >
          {t("trips.edit.unsavedParts.close")}
        </button>
      </div>
    </div>
  );
}

export default function TripEditPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isNewTrip = id === "new";

  // Fetch guides for dropdown
  const { data: guidesData } = useQuery({
    queryKey: ["guides"],
    queryFn: () => api.get<ApiResponse<Guide[]>>("/api/guides"),
  });

  // Fetch all trips for related items picker
  const { data: allTripsData } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips"),
  });

  // Fetch all premium upgrades for selection
  const { data: upgradesData } = useQuery({
    queryKey: ["premiumUpgrades"],
    queryFn: () => api.get<ApiResponse<PremiumUpgrade[]>>("/api/admin/premium-upgrades"),
  });

  // Fetch the priced component catalog (kusovník picker).
  const { data: componentCatalogData } = useQuery({
    queryKey: ["supplierComponentsCatalog"],
    queryFn: () =>
      api.get<ApiResponse<CatalogComponent[]>>("/api/admin/supplier-components?limit=100"),
  });

  // Fetch existing upgrade assignments for this trip
  const { data: upgradeAssignmentsData } = useQuery({
    queryKey: ["tripUpgrades", id],
    queryFn: () => api.get<ApiResponse<TripPremiumUpgrade[]>>(`/api/admin/trips/${id}/upgrades`),
    enabled: !isNewTrip && !!id,
  });

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData, queryClient } = useEditPage<
    TripFormData,
    Trip
  >({
    resourceKey: "trip",
    listKey: "trips",
    apiPath: "/api/admin/trips",
    defaultValues: TRIP_FORM_DEFAULTS,
    navigateTo: "/trips",
    slugSourceField: "title",
    // Reset z detailu nese přiřazení z keše `["tripUpgrades", id]` (jediný zdroj přiřazení):
    // bez nich by každý reset po načtení nebo po uložení vrátil `upgradeIds: []`.
    transformOnLoad: (trip) =>
      tripFormFromDetail(
        trip,
        upgradeAssignmentsData?.data?.map((a) => a.upgradeId),
      ),
    // `upgradeIds` u nového zájezdu vždy (založení je uloží), u existujícího jen s načtenými
    // přiřazeními, jinak klíč chybí a API přiřazení nemění (klíč maže všechna a vkládá nová).
    transformOnSave: (data) =>
      tripSaveBody(data, {
        isNew: isNewTrip,
        upgradeAssignmentsLoaded: upgradeAssignmentsData !== undefined,
      }),
  });

  const guides = guidesData?.data || [];
  const allUpgrades = upgradesData?.data || [];
  const allTrips = (allTripsData?.data || []).filter((t) => t.id !== itemData?.id);
  const componentCatalog = componentCatalogData?.data || [];

  // Přiřazení, která dojedou až po detailu: zapsat do formuláře BEZ `shouldDirty`, ať pozdější
  // reset z detailu (po uložení) neskočí na špinavý formulář. `setValue` zapíše i do pole,
  // které zrovna nikdo neregistruje (záložka Obsah zavřená). Výběr, který redaktor už změnil,
  // refetch nepřepíše (refetch po uložení dorazí i do rozdělané úpravy), stejně jako reset
  // z detailu přeskakuje špinavý formulář.
  useEffect(() => {
    if (upgradeAssignmentsData?.data && !form.getFieldState("upgradeIds").isDirty) {
      const ids = upgradeAssignmentsData.data.map((a) => a.upgradeId);
      form.setValue("upgradeIds", ids);
    }
  }, [upgradeAssignmentsData, form]);

  // Po úspěšném uložení existujícího zájezdu srovnat keš přiřazení (jediný zdroj) s tím, co
  // uložení neslo (`saveMutation.variables` = hodnoty formuláře TOHOTO uložení), a nechat ji
  // obnovit. Reset z detailu pak nese uložená id; bez toho by refetch detailu po uložení vrátil
  // `upgradeIds` ze staré keše a další uložení je smazalo. Nenačtená přiřazení (tělo klíč
  // neneslo) keš nezmění: `assignmentsAfterSave` nad `undefined` vrací `undefined`.
  const savedTrip = saveMutation.data?.data;
  const savedUpgradeIds = saveMutation.variables?.upgradeIds;
  useEffect(() => {
    if (isNewTrip || !id || !savedTrip || savedTrip.id !== id) return;
    const queryKey = ["tripUpgrades", id];
    queryClient.setQueryData<ApiResponse<TripPremiumUpgrade[]>>(queryKey, (prev) =>
      assignmentsAfterSave(prev, id, savedUpgradeIds ?? []),
    );
    void queryClient.invalidateQueries({ queryKey });
    // `variables` patří k téže mutaci jako `data`: nové uložení vymění obojí najednou.
  }, [savedTrip, savedUpgradeIds, id, isNewTrip, queryClient]);

  // Části, které založení zájezdu neuložilo (`unsavedParts` v odpovědi 201). Stav nese id
  // zájezdu: komponenta přežije přechod z `/trips/new` na `/trips/<id>` (pruh se má ukázat
  // až tam), ale i každou jinou změnu `id` v trase, kde pruh nepatří. Zavře ho jen redakce
  // nebo odchod z editoru (odpojení); další uložení ho nezavře. Chyba uložení (400 kapacit)
  // sem nepatří, ukazuje ji `ErrorBanner`.
  const [unsaved, setUnsaved] = useState<{ tripId: string; parts: UnsavedTripPart[] } | null>(null);
  const saveResponse = saveMutation.data;
  useEffect(() => {
    const parts = unsavedTripParts(saveResponse);
    const tripId = saveResponse?.data?.id;
    if (parts.length > 0 && tripId) setUnsaved({ tripId, parts });
  }, [saveResponse]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = form;

  const tabsWithErrors = getTabsWithErrors(errors, TRIP_FIELD_TO_TAB);

  // Hide tabs along the two independent axes (Pre-launch Area 3):
  //  - CONTENT (tripKind): experiences show highlights (no itinerary) + the experience tab.
  //  - BOOKING (fulfillmentMode): on-request books on a calendar, so there is no Dates tab.
  // A multi-day on-request TOUR therefore keeps its itinerary but loses the Dates tab.
  const tripKind = watch("tripKind") || "tour";
  const fulfillmentMode = watch("fulfillmentMode") || "fixed_departures";
  //  - HOTEL (tripType): only a resort has a hotel overlay. The on-request half of isHotelTrip
  //    is a hint inside the tab, not a visibility rule — otherwise the tab would vanish while
  //    the admin is still switching a new trip's booking mode.
  const tripType = watch("tripType");
  const visibleTabs = TABS.filter((t) => {
    if (t.id === "itinerary") return tripKind !== "experience";
    if (t.id === "dates") return fulfillmentMode !== "on_request";
    if (t.id === "experience") return tripKind === "experience";
    if (t.id === "hotel") return tripType === "resort";
    return true;
  });

  // Záložka v adrese (`?tab=`). Musí stát za `visibleTabs` (ty čtou formulář) a před
  // `submitTrip` (volá `setTab`) i před časnými `return` (je to hook). Dokud formulář po
  // načtení detailu nedojede, je `tripType` výchozí a `?tab=hotel` se krátce vykreslí jako
  // první záložka; adresa se ale nepřepíše, takže hotel pak záložku Hotel otevře.
  const [activeTab, setTab] = useEditorTab(visibleTabs.map((tab) => tab.id));

  // Po neúspěšném uložení přepnout na záložku s první chybou pole serveru.
  useSwitchTabOnSaveError(saveMutation.error, TRIP_FIELD_TO_TAB, setTab);

  // Záložky se přepínají VÝMĚNOU komponenty, takže pole neaktivní záložky je odpojené
  // a react-hook-form jeho `validate` při odeslání přeskočí. Souřadnice proto hlídá tenhle
  // obal nad celými daty formuláře, ať uložení z kterékoli záložky neodešle neplatný text.
  // Je to JEDINÁ větev souřadnic v toku uložení: `coordinatesForSave` pak posílá vždy obě
  // hodnoty ověřené týmž `hotelCoordinates` jako Zod, takže serverová chyba dvojice ani
  // rozsahu z tohoto formuláře nevznikne (hlídá ji API pro ostatní klienty).
  const submitTrip = handleSubmit(
    (data) => {
      // Jen u resortu: jinde je záložka Hotel skrytá (`visibleTabs`), `setTab("hotel")` by
      // skončil na první záložce a chyba by zůstala v odpojeném poli, takže by uložení tiše
      // nic neudělalo. Neplatný text mimo resort zahodí `transformOnSave`.
      const coordinatesError =
        data.tripType === "resort" ? coordinateInputError(data.hotelCoordinatesText) : null;
      if (coordinatesError) {
        form.setError("hotelCoordinatesText", { type: "validate", message: coordinatesError });
        setTab("hotel");
        return;
      }
      return onSubmit(data);
    },
    // Klientská chyba (`validate` připojeného pole) nespouští `saveMutation.error`, takže
    // přepnutí na záložku s chybou musí udělat i tahle větev.
    (invalid: FieldErrors<TripFormData>) => {
      const firstTab = getFirstTabWithErrors(invalid, TRIP_FIELD_TO_TAB);
      if (firstTab) setTab(firstTab);
    },
  );

  // Nápovědy a viditelnost polí podle FORMULÁŘE; odkazy „Zobrazit na webu“ podle ULOŽENÉHO
  // řádku: kanonická adresa (hotel na /hotel/…, jinak /zajezd/…) a stav publikace, které web
  // opravdu má. Nepublikovaná změna typu by jinak vedla na adresu, kterou web ještě nezná.
  const variant = useTripHintVariant(form);
  const published = itemData?.status === "published";
  const pageUrl = itemData
    ? WEB_URL +
      routes.tripDetail({
        slug: itemData.slug,
        tripType: itemData.tripType,
        tripKind: itemData.tripKind,
        fulfillmentMode: itemData.fulfillmentMode,
      })
    : null;
  const linkVariant = itemData ? tripVariant(itemData) : variant;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!isNew && !isLoading && !itemData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600 dark:text-gray-400">{t("trips.edit.notFound")}</p>
        <Link to="/trips" className="btn-primary">
          <ArrowLeft className="h-5 w-5" />
          {t("trips.edit.backToTrips")}
        </Link>
      </div>
    );
  }

  // Země okna výběru fotek (F7.11) podle hlavní destinace formuláře (zájezd i hotel), i před uložením.
  const pickerDestinationId = watch("destinationId") || null;

  const page = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/trips" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? t("trips.edit.newTrip") : t("trips.edit.editTrip")}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? t("trips.edit.newSubtitle")
                : t("trips.edit.editingSubtitle", { title: itemData?.title ?? "" })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          <TabWebLink
            target={{ anchor: null }}
            pageUrl={pageUrl}
            published={published}
            isNew={isNewTrip}
          />
          {!isNew && (
            <div className="max-w-xs">
              <select {...register("status")} className="input w-40">
                <option value="draft">{t("trips.edit.statusDraft")}</option>
                <option value="published">{t("trips.edit.statusPublish")}</option>
                <option value="archived">{t("trips.edit.statusArchive")}</option>
              </select>
              <FieldHint registry={tripHintRegistry} field="status" variant={variant} />
            </div>
          )}
          <button
            type="button"
            onClick={submitTrip}
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {t("common.actions.save")}
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />
      {unsaved && unsaved.tripId === id && (
        <UnsavedPartsBanner parts={unsaved.parts} onClose={() => setUnsaved(null)} />
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4 overflow-x-auto">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const hasErrors = tabsWithErrors.has(tab.id);
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {hasErrors && (
                  <span
                    className="inline-flex h-2 w-2 rounded-full bg-red-500"
                    aria-label={t("trips.edit.hasValidationErrors")}
                  />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Form */}
      <form id="trip-form" onSubmit={submitTrip}>
        {/* Mimo `Suspense`, ať se odkaz záložky při načítání lazy záložky neschová. */}
        <div data-tab-web-link={activeTab} className="mb-4">
          <TabWebLink
            target={tabWebTarget(tripHintRegistry, activeTab, linkVariant)}
            pageUrl={pageUrl}
            published={published}
            isNew={isNewTrip}
          />
        </div>
        <Suspense
          fallback={
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
            </div>
          }
        >
          <TripTabContent
            activeTab={activeTab}
            form={form}
            guides={guides}
            allUpgrades={allUpgrades}
            allTrips={allTrips}
            componentCatalog={componentCatalog}
            savedTrip={itemData}
            upgradesHint={
              <FieldHint registry={tripHintRegistry} field="upgradeIds" variant={variant} />
            }
          />
        </Suspense>
      </form>
    </div>
  );

  return (
    <MediaPickerScopeProvider destinationId={pickerDestinationId}>{page}</MediaPickerScopeProvider>
  );
}
